import { api, withQuery } from '@fieldvision/web-core';
import { useQuery } from '@tanstack/react-query';

import { daysBetween } from './format.js';

export interface ClosedPeriod {
  periodMonth: string;
  closedAt: string;
  competitionIndexThreshold: string;
  fraudSlaWarnDays: number;
  fraudSlaDangerDays: number;
}

interface PeriodsResponse {
  periods: ClosedPeriod[];
  graceDays: number;
}

/**
 * Which reporting months are locked — migration 0047.
 *
 * ONE QUERY FOR THE WHOLE CONSOLE, cached long. The answer changes at most once
 * a month and is read by every report screen's filter bar, so refetching it per
 * screen would be four requests for a list of at most sixty short rows. The
 * staleness that buys is harmless in the only direction it can go: a badge that
 * takes a few minutes to appear after a close, on a screen whose numbers stopped
 * moving at the same instant either way.
 *
 * It is `report:read`, the permission every screen showing the badge already
 * holds — see `PeriodsController` on why the lock state is not a secret.
 */
export function useClosedPeriods() {
  return useQuery({
    queryKey: ['reporting', 'periods'],
    queryFn: () => api.get<PeriodsResponse>(withQuery('/reporting/periods', {})),
    staleTime: 5 * 60_000,
  });
}

/**
 * The months a window covers that are already final.
 *
 * WHY THE CONSOLE COMPUTES THIS RATHER THAN ASKING. The filter is a free date
 * range and changes on every keystroke of a date input; a request per range
 * would be a request per keystroke for an answer derived from a list the client
 * already has. The month arithmetic is the same rule the API applies —
 * Asia/Jakarta, first of the month — and the API is still the authority: a write
 * into a closed month is refused by the database whatever this returns.
 *
 * Returns them oldest first, so a caption reads in the order a reader expects.
 */
export function closedMonthsInWindow(
  periods: ClosedPeriod[] | undefined,
  from: string,
  to: string,
): string[] {
  if (!periods?.length || !from || !to) return [];
  // Guard an inverted range rather than looping backwards forever; `windowError`
  // already tells the user about it.
  if (daysBetween(from, to) < 0) return [];

  const start = from.slice(0, 7);
  const end = to.slice(0, 7);
  return periods
    .map((period) => period.periodMonth)
    .filter((month) => {
      const key = month.slice(0, 7);
      return key >= start && key <= end;
    })
    .sort();
}

const MONTHS = [
  'Januari',
  'Februari',
  'Maret',
  'April',
  'Mei',
  'Juni',
  'Juli',
  'Agustus',
  'September',
  'Oktober',
  'November',
  'Desember',
];

/** `2026-08-01` -> `Agustus 2026`. */
export function monthLabel(periodMonth: string): string {
  const [year, month] = periodMonth.split('-').map(Number);
  if (!year || !month) return periodMonth;
  return `${MONTHS[month - 1]} ${year}`;
}
